import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

type Theme = "dark" | "light";

interface ThemeContextProps {
    theme: Theme;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

function applyTheme(t: Theme) {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(t);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>("light");

    useEffect(() => {
        // 1. Check for cookie preference
        const savedTheme = Cookies.get("aadishlabs-theme") as Theme;
        if (savedTheme === "dark" || savedTheme === "light") {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setThemeState(savedTheme);
            applyTheme(savedTheme);
            return;
        }

        // 2. Fallback to OS preference
        const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        const initialTheme = systemPrefersDark ? "dark" : "light";
        setThemeState(initialTheme);
        applyTheme(initialTheme);
    }, []);

    const setTheme = useCallback((t: Theme) => {
        setThemeState(t);
        applyTheme(t);
        // Save preference to cookie (expires in 365 days)
        Cookies.set("aadishlabs-theme", t, { expires: 365 });
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
